import { useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import api from "@/lib/api";
import { toast } from "sonner";
import { Palette, Package, ShoppingBag, Plane, PartyPopper, Snowflake, Loader2, Sparkles, ArrowLeft, SlidersHorizontal } from "lucide-react";

const SERVICES = [
  { key: "color_analysis", icon: Palette, title: "Color Analysis", blurb: "Your most flattering palette + colors to avoid.", params: [] },
  { key: "capsule_wardrobe", icon: Package, title: "Capsule Wardrobe", blurb: "A tight mix-and-match set built on what you own.", params: [] },
  { key: "shopping_recommendations", icon: ShoppingBag, title: "Shopping List", blurb: "Gap analysis — exactly what to buy next.", params: [] },
  { key: "event_look", icon: PartyPopper, title: "Event Look", blurb: "A head-to-toe fit for any occasion & dress code.",
    params: [{ name: "event", label: "Event", placeholder: "friend's rooftop birthday" }, { name: "dress_code", label: "Dress code", placeholder: "smart casual" }] },
  { key: "packing_list", icon: Plane, title: "Travel Packing", blurb: "Day-by-day outfits, packed light.",
    params: [{ name: "destination", label: "Destination", placeholder: "Miami" }, { name: "days", label: "Days", placeholder: "4" }, { name: "occasions", label: "Occasions", placeholder: "beach, dinners, club" }] },
  { key: "seasonal_refresh", icon: Snowflake, title: "Seasonal Refresh", blurb: "Restyle for the season + key buys.",
    params: [{ name: "season", label: "Season", placeholder: "fall" }] },
];

export default function Services() {
  const [active, setActive] = useState(null);
  const [params, setParams] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");

  const open = (svc) => {
    setActive(svc);
    setParams({});
    setResult("");
  };

  const generate = async () => {
    setLoading(true);
    setResult("");
    try {
      const { data } = await api.post("/stylist/service", { service_type: active.key, params });
      setResult(data.result);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-bg0 min-h-screen">
      <Navbar />
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-12">
        <div className="flex items-end justify-between gap-4 mb-10">
          <div>
            <div className="overline mb-3 text-volt">Stylist Services</div>
            <h1 className="font-display uppercase text-6xl md:text-7xl font-bold tracking-tighter">Studio</h1>
          </div>
          <Link to="/profile" data-testid="goto-profile" className="ep-btn inline-flex items-center gap-2 border border-white/25 px-5 py-3 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black">
            <SlidersHorizontal size={15} /> Style Profile
          </Link>
        </div>

        {!active ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="services-grid">
            {SERVICES.map((s) => (
              <button key={s.key} onClick={() => open(s)} data-testid={`service-${s.key}`}
                className="ep-card p-7 text-left group">
                <s.icon size={26} strokeWidth={1.5} className="text-volt" />
                <h3 className="font-display uppercase text-2xl font-semibold tracking-tight mt-6">{s.title}</h3>
                <p className="text-epsub text-sm mt-2 leading-relaxed">{s.blurb}</p>
                <span className="overline text-volt mt-5 inline-flex items-center gap-1 group-hover:gap-2 transition-all">Open →</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="service-panel">
            <div className="ep-card p-7 h-fit">
              <button onClick={() => setActive(null)} data-testid="service-back" className="ep-btn inline-flex items-center gap-2 overline text-epmuted hover:text-white mb-5">
                <ArrowLeft size={14} /> All services
              </button>
              <active.icon size={28} strokeWidth={1.5} className="text-volt" />
              <h2 className="font-display uppercase text-3xl font-bold tracking-tight mt-4">{active.title}</h2>
              <p className="text-epsub text-sm mt-2 leading-relaxed">{active.blurb}</p>

              {active.params.map((p) => (
                <div key={p.name} className="mt-5">
                  <label className="overline block mb-2">{p.label}</label>
                  <input
                    value={params[p.name] || ""}
                    onChange={(e) => setParams({ ...params, [p.name]: e.target.value })}
                    placeholder={p.placeholder}
                    data-testid={`param-${p.name}`}
                    className="w-full bg-transparent border-b-2 border-epborder focus:border-volt px-0 py-2.5 text-white placeholder:text-zinc-600 transition-colors"
                  />
                </div>
              ))}

              <button onClick={generate} disabled={loading} data-testid="service-generate"
                className="ep-btn w-full mt-7 inline-flex items-center justify-center gap-2 bg-volt text-black py-3.5 text-sm font-bold uppercase tracking-widest hover:bg-volt-hover disabled:opacity-60">
                {loading ? <><Loader2 size={16} className="animate-spin" /> Working…</> : <><Sparkles size={16} /> Generate</>}
              </button>
            </div>

            <div className="lg:col-span-2 ep-card p-8 min-h-[50vh]" data-testid="service-result">
              {loading && (
                <div className="space-y-3">
                  {[...Array(6)].map((_, i) => <div key={i} className="h-4 ep-loading" style={{ width: `${70 + (i % 3) * 10}%` }} />)}
                </div>
              )}
              {!loading && !result && (
                <div className="h-full flex flex-col items-center justify-center text-center py-16">
                  <active.icon size={30} className="text-volt" />
                  <p className="font-display uppercase text-2xl font-semibold tracking-tight mt-4">Ready when you are.</p>
                  <p className="text-epsub text-sm mt-2 max-w-sm">Hit Generate and Eternal will craft your {active.title.toLowerCase()}.</p>
                </div>
              )}
              {!loading && result && (
                <div className="whitespace-pre-wrap text-sm leading-relaxed text-white/90 font-body" data-testid="service-result-text">
                  {result}
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
